import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { MagnifyingGlassIcon, UserPlusIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@libs/zapi-ui/ds/components/empty";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { hasEditAccess, isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { getAccessModes, getMemberRoles, memberStatuses, STATUS_KEYS } from "../const/const";
import { getInitials } from "../lib/getInitials";
import { getInviteFormSchema } from "../lib/getInviteFormSchema";
import { getOrganizationData } from "../services/getOrganizationData";
import { sendInvite } from "../services/sendInvite";
import { updateUser } from "../services/updateUser";
import { useRevokeInvitation } from "../services/useRevokeInvitation";
import { useResendInvitation } from "../services/useResendInvitation";
import { TAccessMode, TGetUsersResponse, TUser, TUserRole } from "../services/schemas";
import { InviteMemberDialog, type InviteMemberResult, type InviteMemberValues } from "./blocks/InviteMemberDialog";
import { MemberAccessSheet, type MemberAccessValues } from "./blocks/MemberAccessSheet";
import { SettingsError, SettingsHeader, SettingsSection } from "./blocks/SettingsLayout";
import {
  TeamMemberList,
  TeamMemberListSkeleton,
  TeamMemberSearch,
  type TeamMember,
  type TeamMemberAction
} from "./blocks/TeamMembers";

const FIXED_ACCESS: Record<string, TAccessMode> = { systemAdmin: "edit" };

const isInvitation = (user: TUser) => user.status === "pending" || user.status === "expired";

export function TeamSection() {
  const { t, i18n } = useTranslation(["settings", "common"]);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAccessSheetOpen, setIsAccessSheetOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<TUser | null>(null);
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [busyActions, setBusyActions] = useState<Partial<Record<string, TeamMemberAction>>>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organizationId: defaultOrganizationId, id: currentUserId, role, accessMode, organizationName } = useMeStore();
  const { currentOrganizationId } = useOrganizationStore();
  const { mutate: cancelInvitation } = useRevokeInvitation();
  const { mutate: resendInvitation } = useResendInvitation();

  const organizationId =
    isSystemAdmin(role) && currentOrganizationId > 0 ? currentOrganizationId : defaultOrganizationId;
  const currentUserHasEditAccess = hasEditAccess(role, accessMode);

  const {
    data: organizationData,
    isLoading: isLoadingOrganizationData,
    isError,
    refetch
  } = useQuery<TGetUsersResponse, Error>({
    queryKey: ["organizationData", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      try {
        const response = await getOrganizationData(organizationId);
        return response.map((user) => ({ ...user, createdAt: new Date(user.createdAt) }));
      } catch (error) {
        console.error(error);
        toast.error(t("teamTable.fetchFailed"));
        navigate(getRouteProjects());
        throw error;
      }
    },
    enabled: !!organizationId
  });

  const refreshTeam = () => queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });

  const filteredTeamMembers = useMemo(() => {
    if (!organizationData) return [];
    if (!deferredSearchQuery) return organizationData;
    return organizationData.filter(
      (member) =>
        member.firstName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.email.toLowerCase().startsWith(deferredSearchQuery.toLowerCase())
    );
  }, [deferredSearchQuery, organizationData]);

  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }), [i18n.language]);
  const roles = getMemberRoles(t, role);
  const accessModes = getAccessModes(t);
  const allRoles = getMemberRoles(t, "systemAdmin");

  const toMember = (user: TUser): TeamMember => {
    const invited = isInvitation(user);
    const date = dateFormat.format(user.createdAt);
    const accessLabel = accessModes.find((mode) => mode.value === user.accessMode)?.label ?? user.accessMode;
    return {
      id: String(user.id),
      name: `${user.firstName} ${user.lastName}`.trim(),
      email: user.email,
      initials: getInitials(user.firstName, user.lastName),
      role: allRoles.find((candidate) => candidate.value === user.role)?.label ?? user.role,
      access: isSystemAdmin(user.role) ? undefined : t("redesign.team.access", { access: accessLabel }),
      projects: invited ? undefined : t("redesign.team.projects", { count: user.projects?.length ?? 0 }),
      status: user.status,
      statusLabel: t(STATUS_KEYS[user.status]),
      since: invited ? t("redesign.team.invited", { date }) : t("redesign.team.joined", { date }),
      isYou: user.id === currentUserId
    };
  };

  const isOnlyMember =
    !!organizationData &&
    organizationData.length === 1 &&
    organizationData[0]?.id === currentUserId &&
    !deferredSearchQuery;

  const setMemberBusy = (id: number, action?: TeamMemberAction) =>
    setBusyActions((current) => ({ ...current, [String(id)]: action }));

  function handleMemberAction(member: TeamMember, action: TeamMemberAction) {
    const user = organizationData?.find((candidate) => String(candidate.id) === member.id);
    if (!user || !organizationId) return;
    if (action === "manage") {
      setSelectedUserForAccess(user);
      setIsAccessSheetOpen(true);
      return;
    }
    setMemberBusy(user.id, action);
    const variables = { organizationId, email: user.email };
    const onSettled = () => setMemberBusy(user.id);
    if (action === "resend") {
      resendInvitation(variables, {
        onSettled,
        onSuccess: () => {
          toast.success(t("team.inviteResent"));
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? t("team.inviteResendFailed"));
        }
      });
      return;
    }
    cancelInvitation(variables, {
      onSettled,
      onSuccess: () => {
        refreshTeam();
        toast.success(t("team.inviteCancelled"));
      },
      onError: (error) => {
        toast.error(error.response?.data.message ?? t("team.inviteCancelFailed"));
      }
    });
  }

  async function handleInvite(formData: InviteMemberValues): Promise<InviteMemberResult> {
    const validatedFields = getInviteFormSchema(t).safeParse(formData);
    if (!validatedFields.success) {
      const fieldErrors = validatedFields.error.flatten().fieldErrors;
      toast.error(t("invite.invalidForm"));
      return {
        ok: false,
        errors: {
          role: formData.role ? fieldErrors.role?.[0] : t("invite.roleRequired"),
          firstName: fieldErrors.firstName?.[0],
          lastName: fieldErrors.lastName?.[0],
          email: formData.email.trim() ? fieldErrors.email?.[0] : t("invite.emailRequired")
        }
      };
    }
    if (!organizationId) return { ok: false };

    const [, error] = await tryCatch<null, AxiosError<{ message: string }>>(
      sendInvite({
        orgId: organizationId.toString(),
        email: formData.email,
        role: formData.role,
        accessMode: formData.accessMode as TAccessMode,
        firstName: formData.firstName,
        lastName: formData.lastName
      })
    );
    if (error) {
      toast.error(error.response?.data.message || t("invite.sendFailed"));
      console.error("Error sending invite:", error);
      return { ok: false };
    }

    refreshTeam();
    toast.success(t("invite.sent"));
    return { ok: true };
  }

  async function handleSaveAccess(values: MemberAccessValues) {
    const user = selectedUserForAccess;
    if (!user || !organizationId) return;
    if (values.role === user.role && values.accessMode === user.accessMode && values.status === user.status) {
      toast.message(t("manageAccess.noChanges"));
      setIsAccessSheetOpen(false);
      return;
    }
    try {
      setIsSavingAccess(true);
      await updateUser({
        orgId: organizationId,
        userId: user.id,
        role: values.role as TUserRole,
        accessMode: values.accessMode as TAccessMode,
        active: values.status === "active"
      });
      refreshTeam();
      toast.success(t("manageAccess.updated"));
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(t("manageAccess.updateFailed"));
    } finally {
      setIsSavingAccess(false);
      setIsAccessSheetOpen(false);
    }
  }

  const inviteButton = (
    <Button shape="pill" data-testid="team-table-invite-user-button" onClick={() => setIsInviteModalOpen(true)}>
      <UserPlusIcon data-icon="inline-start" />
      {t("redesign.team.invite")}
    </Button>
  );

  return (
    <>
      <SettingsHeader title={t("tabs.team")} actions={inviteButton} />
      {isError ? (
        <SettingsError
          title={t("redesign.loadFailed")}
          description={t("teamTable.fetchFailed")}
          retryLabel={t("redesign.retry")}
          onRetry={() => void refetch()}
        />
      ) : (
        <SettingsSection
          title={t("redesign.team.members")}
          actions={
            <TeamMemberSearch
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("redesign.team.search")}
              clearLabel={t("redesign.team.clearSearch")}
            />
          }
        >
          {isLoadingOrganizationData || !organizationId ? (
            <TeamMemberListSkeleton />
          ) : (
            <TeamMemberList
              members={filteredTeamMembers.map(toMember)}
              busy={busyActions}
              onAction={handleMemberAction}
              actionsLabel={(member) => t("redesign.team.actionsFor", { name: member.name })}
              labels={{
                list: t("redesign.team.members"),
                member: t("redesign.team.member"),
                role: t("redesign.team.role"),
                status: t("redesign.team.status"),
                you: t("redesign.team.you"),
                manage: t("redesign.team.manage"),
                resend: t("redesign.team.resend"),
                revoke: t("redesign.team.revoke"),
                loading: t("common:loading")
              }}
              footer={
                deferredSearchQuery && filteredTeamMembers.length > 0
                  ? t("redesign.team.showing", {
                      shown: filteredTeamMembers.length,
                      total: organizationData?.length ?? 0
                    })
                  : undefined
              }
              empty={
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <MagnifyingGlassIcon />
                    </EmptyMedia>
                    <EmptyTitle>{t("redesign.team.noMatch", { query: deferredSearchQuery })}</EmptyTitle>
                    <EmptyDescription>{t("redesign.team.noMatchHint")}</EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button variant="outline" shape="pill" onClick={() => setSearchQuery("")}>
                      {t("redesign.team.clearSearch")}
                    </Button>
                  </EmptyContent>
                </Empty>
              }
            />
          )}
          {isOnlyMember && (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <UsersThreeIcon />
                </EmptyMedia>
                <EmptyTitle>{t("redesign.team.alone")}</EmptyTitle>
                <EmptyDescription>{t("redesign.team.aloneHint")}</EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button shape="pill" onClick={() => setIsInviteModalOpen(true)}>
                  <UserPlusIcon data-icon="inline-start" />
                  {t("redesign.team.invite")}
                </Button>
              </EmptyContent>
            </Empty>
          )}
        </SettingsSection>
      )}

      <InviteMemberDialog
        open={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
        roles={roles}
        accessModes={accessModes}
        defaultAccessMode={currentUserHasEditAccess ? "edit" : "read"}
        canChooseAccess={currentUserHasEditAccess}
        fixedAccess={FIXED_ACCESS}
        onSubmit={handleInvite}
        labels={{
          title: t("redesign.invite.title"),
          description: t("redesign.invite.description", { organization: organizationName ?? "" }),
          role: t("redesign.invite.role"),
          rolePlaceholder: t("redesign.invite.rolePlaceholder"),
          access: t("redesign.invite.access"),
          accessPlaceholder: t("redesign.invite.accessPlaceholder"),
          firstName: t("redesign.invite.firstName"),
          firstNamePlaceholder: t("invite.firstNamePlaceholder"),
          lastName: t("redesign.invite.lastName"),
          lastNamePlaceholder: t("invite.lastNamePlaceholder"),
          email: t("redesign.invite.email"),
          emailPlaceholder: t("invite.emailPlaceholder"),
          submit: t("redesign.invite.send"),
          cancel: t("common.cancel"),
          close: t("common:close"),
          loading: t("common:loading")
        }}
      />
      <MemberAccessSheet
        open={isAccessSheetOpen}
        onOpenChange={setIsAccessSheetOpen}
        member={selectedUserForAccess ? toMember(selectedUserForAccess) : null}
        initialValues={{
          role: selectedUserForAccess?.role ?? "",
          accessMode: selectedUserForAccess?.accessMode ?? "",
          status: selectedUserForAccess?.status ?? ""
        }}
        roles={roles}
        accessModes={accessModes.map((mode) => ({
          ...mode,
          description: t(mode.value === "edit" ? "redesign.access.editHint" : "redesign.access.readHint")
        }))}
        statuses={memberStatuses(t)}
        fixedAccess={FIXED_ACCESS}
        accessLocked={!currentUserHasEditAccess || selectedUserForAccess?.id === currentUserId}
        saving={isSavingAccess}
        onSave={(values) => void handleSaveAccess(values)}
        labels={{
          title: t("redesign.access.title"),
          description: t("redesign.access.description", {
            name: selectedUserForAccess ? `${selectedUserForAccess.firstName} ${selectedUserForAccess.lastName}` : ""
          }),
          role: t("redesign.access.role"),
          access: t("redesign.access.access"),
          accessLocked: t("redesign.access.accessLocked"),
          status: t("redesign.access.status"),
          save: t("common.save"),
          cancel: t("common.cancel"),
          close: t("common:close"),
          loading: t("common:loading")
        }}
      />
    </>
  );
}
